import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
} from '@mui/material';

interface PrivacySection {
  title: string;
  subtitle?: string;
  content: string[];
}

interface PrivacyPolicyData {
  title: string;
  lastUpdatedDate: string;
  sections: PrivacySection[];
}

const privacyPolicy: PrivacyPolicyData = {
  title: 'Privacy Policy',
  lastUpdatedDate: 'January 15, 2025',
  sections: [
    {
      title: '1. Information We Collect',
      content: [
        'We collect personal information that you voluntarily provide to us when contacting us for manufacturing services, requesting quotes, or otherwise interacting with Monio Manufacturing.',
        'The personal information that we collect includes your name, email address, phone number, company information, project requirements, and any other details you provide when seeking our manufacturing services.',
        'We also collect technical information about your visit to our website, including your IP address, browser type, and pages visited.',
      ],
    },
    {
      title: '2. How We Use Your Information',
      content: [
        'We use your information to provide manufacturing services, process quote requests, and communicate with you about your projects.',
        'We may use your information to improve our manufacturing processes, develop new products, and enhance our customer service.',
        'We use technical information to analyze website usage and improve our online presence.',
      ],
    },
    {
      title: '3. Information Sharing and Disclosure',
      content: [
        'We do not sell or rent your personal information to third parties.',
        'We may share your information with trusted suppliers and partners who assist us in providing manufacturing services.',
        'We may disclose information if required by law or to protect our rights and safety.',
      ],
    },
    {
      title: '4. Your Rights and Choices',
      content: [
        'You have the right to access, correct, or delete your personal information.',
        'You can control your communication preferences and opt out of marketing communications.',
        'You may request information about how we process your data and request data portability.',
      ],
    },
    {
      title: '5. Data Security',
      subtitle: 'Protecting Your Information',
      content: [
        'We implement industry-standard security measures to protect your personal and business information.',
        'We maintain strict access controls and regularly update our security protocols.',
        'All data transmission is encrypted using secure protocols.',
      ],
    },
    {
      title: '6. Manufacturing Data',
      content: [
        'We collect and process technical specifications, design files, and project requirements to provide manufacturing services.',
        'This data is treated with the same level of security and confidentiality as personal information.',
        'We retain manufacturing data only as long as necessary to fulfill our obligations and legal requirements.',
      ],
    },
    {
      title: '7. International Data Transfers',
      content: [
        'As a manufacturing company serving clients across Africa and internationally, your data may be transferred to other countries.',
        'We ensure that all international data transfers comply with applicable data protection laws.',
        'We implement appropriate safeguards to protect your data during international transfers.',
      ],
    },
    {
      title: '8. Changes to This Policy',
      content: [
        'We may update this privacy policy from time to time to reflect changes in our practices or legal requirements.',
        'We will notify you of any significant changes through our website or direct communication.',
        'Continued use of our services after changes constitutes acceptance of the updated policy.',
      ],
    },
  ],
};

export const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      pt: 12,
      pb: 16,
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
          color: 'white',
          py: 8,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 3,
              textAlign: 'center',
            }}
          >
            {privacyPolicy.title}
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, opacity: 0.9, textAlign: 'center' }}>
            Last updated: {privacyPolicy.lastUpdatedDate}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.8, textAlign: 'center' }}>
            At Monio Manufacturing, we are committed to protecting your privacy and ensuring the security of your personal and business information.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Typography variant="body1" paragraph sx={{ mb: 6, fontSize: '1.1rem', lineHeight: 1.7 }}>
          This Privacy Policy outlines how Monio Manufacturing collects, uses, and protects your data when you interact with our manufacturing services, 
          request quotes, or visit our website. We are committed to transparency and ensuring your privacy rights are respected.
        </Typography>

        <Grid container spacing={4}>
          {privacyPolicy.sections.map((section, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Box sx={{ p: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{
                    background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600,
                    mb: 2,
                  }}>
                    {section.title}
                  </Typography>
                  {section.subtitle && (
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 500 }}>
                      {section.subtitle}
                    </Typography>
                  )}
                  {section.content.map((paragraph, pIndex) => (
                    <Typography key={pIndex} paragraph sx={{ 
                      lineHeight: 1.7,
                      color: 'text.secondary',
                      mb: 2,
                    }}>
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Contact Information */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Questions About Privacy?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            If you have any questions about this Privacy Policy or how we handle your data, please contact our privacy team.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Email:</strong> privacy@monio.com
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Phone:</strong> +254 700 000 000
          </Typography>
          <Typography variant="body1">
            <strong>Address:</strong> Monio Manufacturing, Nairobi, Kenya
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
